import { Alert, Anchor, Card, Paragraph, Text, TextButton } from '@zillow/constellation';
import { logError } from '@zg-rentals/log-error';
import type { Answers, UserStatus } from '../../../../../graphql/queries';
import { GetRentalApplicationDocument, UserStatusType } from '../../../../../graphql/queries';
import {
  getIsIdentityNotFound,
  getIsPaymentExpired,
  getIsPaymentValid,
  getPaymentExpirationDate,
} from '../../../../../utils/user-statuses';
import { useRouter } from 'next/router';
import { FIELD_IDS } from '../../../../../constants/wizard';
import { getAnswersForFields } from '../../../../../utils/overview/overview-display';
import { ReportAlert } from '../../../../Reports/BackgroundReport/BackgroundReportAlert';
import { useGetReportsForApplication } from '../../../../../hooks/useGetReports';
import type { ReportType } from '../../../../../models';
import { formatDate } from '../../../../../utils/formatters';
import { CreditReportOverview } from '../../../../Reports/ReportCards/CreditReportOverview';
import { BackgroundReportOverview } from '../../../../Reports/ReportCards/BackgroundReportOverview';
import { useCappsScreeningReportsWebApiHandler_createReports } from '@zg-rentals/ts-capps-client-react-query';
import { useEffect, useState } from 'react';
import { getHasNonExpiredReports, transformReportData } from '../../../../../utils/reports/report-utils';
import { useApolloClient } from '@apollo/client';
import { BlockLoadingMask } from '../../../../../styles/styled-components';
import { APPLICATIONS_FAQ_URL } from '../../../../../constants/urls';
import { REQUESTING_CLIENT } from '../../../../../constants/api';

interface Props {
  answers: Answers;
  userStatuses: Array<UserStatus>;
}

const fieldsInStep = [FIELD_IDS.BACKGROUND_REPORT_COMMENT, FIELD_IDS.CREDIT_REPORT_COMMENT];

export function PaymentAndReportsOverview({ answers, userStatuses }: Props) {
  const router = useRouter();
  const apolloClient = useApolloClient();
  const isIdentityNotFound = getIsIdentityNotFound(userStatuses);
  const answersForFields = getAnswersForFields({ fieldIds: fieldsInStep, answers });
  const isPaymentExpired = getIsPaymentExpired(userStatuses);
  const isPaymentValid = getIsPaymentValid(userStatuses);
  const paymentExpirationDate = getPaymentExpirationDate(userStatuses);
  const hasNonExpiredReports = getHasNonExpiredReports(userStatuses);
  const createReportsMutation = useCappsScreeningReportsWebApiHandler_createReports();
  const [creatingReportsError, setCreatingReportsError] = useState(false);

  const { data, isPending } = useGetReportsForApplication();
  const reportData = transformReportData(data);

  async function createReports() {
    setCreatingReportsError(false);
    try {
      await createReportsMutation.mutateAsync({
        postData: {
          requestingClient: REQUESTING_CLIENT,
        },
      });
      await apolloClient.refetchQueries({ include: [GetRentalApplicationDocument] });
    } catch (e) {
      logError({ error: e, context: 'PaymentAndReportsOverview - createReportsMutation error' });
      setCreatingReportsError(true);
    }
  }

  useEffect(() => {
    if (isPaymentValid && !hasNonExpiredReports) {
      createReports();
    }
  }, [hasNonExpiredReports, isPaymentValid]);

  function navigateToReport(reportType: ReportType) {
    const { pathname, query } = router;
    router.push({ pathname, query: { ...query, reportType } });
  }

  if (creatingReportsError) {
    return (
      <Alert
        appearance="error"
        body={
          <Text>
            There was an error generating your reports. If the problem continues, contact{' '}
            <Anchor href={APPLICATIONS_FAQ_URL} target="_blank">
              customer support
            </Anchor>
            .
          </Text>
        }
        actionButton={<TextButton onClick={createReports}>Try again</TextButton>}
      />
    );
  }

  return isPaymentValid ? (
    <BlockLoadingMask loading={!hasNonExpiredReports || isPending}>
      {paymentExpirationDate && (
        <Paragraph>Your screening reports are valid until {formatDate(paymentExpirationDate)}</Paragraph>
      )}
      <ReportAlert reportData={reportData} marginY="sm" />
      <Card cardElevation={0} cardType="secondary" marginTop="sm">
        <CreditReportOverview
          creditReport={reportData?.creditReport}
          userStatuses={userStatuses}
          onView={() => navigateToReport(UserStatusType.creditReport)}
        />
        {answersForFields[FIELD_IDS.CREDIT_REPORT_COMMENT] && (
          <Paragraph marginTop="xs">{answersForFields[FIELD_IDS.CREDIT_REPORT_COMMENT]}</Paragraph>
        )}
      </Card>
      <Card cardElevation={0} cardType="secondary" marginTop="sm">
        <BackgroundReportOverview
          backgroundReport={reportData?.backgroundReport}
          userStatuses={userStatuses}
          onView={() => navigateToReport(UserStatusType.backgroundReport)}
        />
        {answersForFields[FIELD_IDS.BACKGROUND_REPORT_COMMENT] && (
          <Paragraph marginTop="xs">{answersForFields[FIELD_IDS.BACKGROUND_REPORT_COMMENT]}</Paragraph>
        )}
      </Card>
    </BlockLoadingMask>
  ) : isPaymentExpired ? (
    <Paragraph>Your screening reports have expired</Paragraph>
  ) : isIdentityNotFound ? (
    <Paragraph>
      Reports unavailable. Please verify your identity or continue without reports to submit your application.
    </Paragraph>
  ) : null;
}
